import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';

export interface ModalProps {
    isOpen?: boolean;
    title?: string;
    onClose: () => void;
    children: React.ReactNode;
    size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
    showHeader?: boolean;
    headerIcon?: React.ReactNode;
    headerDescription?: string;
}

const Modal: React.FC<ModalProps> = ({
    isOpen = true,
    title,
    onClose,
    children,
    size = 'md',
    showHeader = true,
    headerIcon,
    headerDescription
}) => {
    const modalRef = useRef<HTMLDivElement>(null);
    
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        }
        
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isOpen, title]);

    if (!isOpen) {
        return null;
    }


    // Enhanced size classes with responsive design
    const sizeClasses = {
        sm: 'max-w-md',
        md: 'max-w-lg',
        lg: 'max-w-4xl',
        xl: 'max-w-6xl',
        '2xl': 'max-w-6xl'
    };

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleModalClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const modalContent = (
        <div className="fixed inset-0 z-[10000] overflow-y-auto">
            {/* Enhanced Backdrop */}
            <div 
                className="fixed inset-0 bg-gradient-to-br from-black/60 to-black/40 backdrop-blur-sm transition-all duration-300"
                onClick={handleBackdropClick}
            ></div>

            {/* Modal Container */}
            <div className="flex min-h-full items-center justify-center p-2 sm:p-4">
                <div 
                    ref={modalRef}
                    className={`relative bg-white rounded-2xl sm:rounded-3xl w-full ${sizeClasses[size]} mx-2 sm:mx-4 max-h-[90vh] sm:max-h-[95vh] overflow-hidden shadow-2xl border border-gray-100`}
                    onClick={handleModalClick}
                >
                    {/* Enhanced Header */}
                    {showHeader && (
                        <div className="sticky top-0 z-10 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 sm:px-8 py-4 sm:py-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2 sm:space-x-4">
                                    {headerIcon && (
                                        <div className="p-2 sm:p-3 bg-white/20 rounded-xl sm:rounded-2xl backdrop-blur-sm">
                                            {headerIcon}
                                        </div>
                                    )}
                                    <div>
                                        {title && (
                                            <h3 className="text-lg sm:text-2xl font-bold">
                                                {title}
                                            </h3>
                                        )}
                                        {headerDescription && (
                                            <p className="text-primary-100 text-xs sm:text-sm hidden sm:block">
                                                {headerDescription}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="p-2 sm:p-3 text-white/80 hover:text-white hover:bg-white/20 rounded-xl sm:rounded-2xl transition-all duration-200"
                                >
                                    <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Content with proper scrolling */}
                    <div className={`${showHeader ? 'bg-gray-50' : 'bg-white'} ${showHeader ? '' : 'rounded-2xl sm:rounded-3xl'} ${showHeader ? 'max-h-[calc(90vh-120px)] sm:max-h-[calc(95vh-140px)]' : 'max-h-[90vh] sm:max-h-[95vh]'} overflow-y-auto`}>
                        <div className={`${showHeader ? 'p-4 sm:p-8' : 'p-6 sm:p-8'}`}>
                            {children}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Use portal to render modal at document.body level
    return createPortal(modalContent, document.body);
};

export default Modal;